import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  clearRefreshToken,
  readRefreshToken,
  request,
  setAccessToken,
  setSessionLostHandler,
  writeRefreshToken,
} from '../api/client';
import { api } from '../api/shop';
import type { AuthTokens } from '../api/types';

/**
 * Who is signed in.
 *
 * The access token is never held in React state and never in storage — it lives in
 * a module variable inside the API client. Putting it in state would mean it ends up
 * in a devtools snapshot and, with SSR, in serialised HTML; putting it in
 * localStorage would mean any script on the origin can read it.
 *
 * What IS in state is the fact of being signed in, which is not a secret.
 */

interface AuthState {
  userId: string | null;
  ready: boolean;

  /**
   * Whether this account has a password. Comes back on every sign-in and every
   * refresh, so it survives a reload without being stored anywhere.
   */
  hasPassword: boolean;

  signIn: (tokens: AuthTokens) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const USER_KEY = 'nf.uid';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [hasPassword, setHasPassword] = useState(false);

  // False until the stored session has been checked, so a protected page does not
  // flash "please sign in" at somebody who already is.
  const [ready, setReady] = useState(false);

  const signIn = useCallback((tokens: AuthTokens) => {
    setAccessToken(tokens.accessToken);
    writeRefreshToken(tokens.refreshToken);

    try {
      window.sessionStorage.setItem(USER_KEY, tokens.userId);
    } catch {
      /* storage disabled; the session simply does not survive a reload */
    }

    setUserId(tokens.userId);
    setHasPassword(tokens.hasPassword);
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = readRefreshToken();

    if (refreshToken) {
      // Best effort. The local session is cleared either way — a failed network
      // call must never leave somebody looking signed in on a shared device.
      try {
        await api.logout(refreshToken);
      } catch {
        /* server-side revocation failed; local sign-out still stands */
      }
    }

    setAccessToken(null);
    clearRefreshToken();

    try {
      window.sessionStorage.removeItem(USER_KEY);
    } catch {
      /* nothing to clear */
    }

    setUserId(null);
    setHasPassword(false);
  }, []);

  // Restore on load: swap the stored refresh token for a fresh access token.
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      const stored = readRefreshToken();

      if (!stored) {
        setReady(true);
        return;
      }

      try {
        const tokens = await request<AuthTokens>('/api/v1/auth/refresh', {
          method: 'POST',
          body: { refreshToken: stored },
          skipRefresh: true,
        });

        if (!cancelled) signIn(tokens);
      } catch {
        // Expired, revoked, or the family was killed by reuse detection.
        clearRefreshToken();
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, [signIn]);

  // The API client signals here when a refresh fails mid-session, so one place
  // decides what "signed out" means.
  useEffect(() => {
    setSessionLostHandler(() => {
      setAccessToken(null);
      clearRefreshToken();
      setUserId(null);
      setHasPassword(false);
    });
  }, []);

  const value = useMemo<AuthState>(
    () => ({ userId, ready, hasPassword, signIn, signOut }),
    [userId, ready, hasPassword, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error('useAuth must be used inside <AuthProvider>.');
  }

  return context;
}
